import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTheme } from "styled-components";
import {
  FiAlertTriangle, FiBookOpen, FiChevronRight, FiGrid, FiInfo, FiList, FiRefreshCw, FiSearch, FiX,
} from "react-icons/fi";
import { GoOrganization } from "react-icons/go";
import api from "../../services/api";
import {
  CardGrid, EntityCard, Empty, Group, GroupLabel, IconButton, LoadMore, Meter, Row, SearchField, Section,
  SectionHeader, Skeleton, Widget, WidgetGrid, tokens,
} from "../activity-logs/styles";
import { SegmentedControl } from "../activity-logs/components/primitives";
import { ActionButton, CreditsPage, Notice, SpanCell, StepNumber, Tag, TruncatedText } from "./styles";
import {
  categoryMeta, displayName, formatCNPJ, formatCompact, formatCount, formatCurrency, formatMonth, formatPercent, taxMeta,
} from "./dashboardData";
import { buildCreditoStats, formatDate, formatRate } from "./detailData";
import { TrendChart } from "./components/charts";
import { BackButton, MoneyValue, RankRows, SupplierAvatar, ScrolledTopBar } from "./components/parts";
import FornecedorDetailScreen from "./FornecedorDetailScreen";

const PAGE_SIZE = 60;

/**
 * Dashboard de um tipo de crédito: de onde o valor vem e quão concentrado ele é.
 * Totais e fornecedores vêm de /creditos/agrupado/:tipo, que não filtra por data.
 * A evolução usa a série mensal já consultada pela tela principal.
 */

function Overview({ stats, credito, metodologia, trend, trendLoading, periodFiltered, onOpenSupplier, onGoSuppliers, onGoMethod }) {
  const theme = useTheme();
  const { color } = taxMeta(credito.tipo);
  const [cut, setCut] = useState("cfop");
  const peak = trend.reduce((best, m) => (!best || m.valor > best.valor ? m : best), null);
  const top = stats.sorted.slice(0, 8);
  const topMax = Math.max(1, top[0]?.totalCredito || 0);
  const hasUf = stats.byUf.length > 0;
  const cutRows = cut === "uf" && hasUf ? stats.byUf : stats.byCfop;
  const supplierCount = stats.sorted.length;

  return (
    <>
      <Section $first>
        <WidgetGrid>
          <Widget $delay={0}>
            <div className="w-head">Crédito identificado</div>
            <div className="w-value"><MoneyValue value={stats.total} /></div>
            <div className="w-foot">{formatCount(stats.items)} itens</div>
          </Widget>
          <Widget $delay={50}>
            <div className="w-head">Base de cálculo</div>
            <div className="w-value"><MoneyValue value={stats.base} /></div>
            <div className="w-foot">valor dos documentos</div>
          </Widget>
          <Widget $delay={100}>
            <div className="w-head">Alíquota efetiva</div>
            <div className="w-value" style={{ color }}>{formatRate(stats.effectiveRate)}</div>
            <div className="w-foot">crédito ÷ base</div>
          </Widget>
          <Widget $delay={150} $clickable onClick={onGoSuppliers}>
            <div className="w-head">Fornecedores</div>
            <div className="w-value">{formatCount(supplierCount)}</div>
            <div className="w-foot">
              {stats.firstDate ? `${formatMonth(stats.firstDate.slice(0, 7))} — ${formatMonth(stats.lastDate.slice(0, 7))}` : "ver todos"}
            </div>
          </Widget>
        </WidgetGrid>
      </Section>

      <Section $delay={80}>
        <WidgetGrid>
          <Widget $span={3} $delay={150}>
            <div className="w-title">Evolução do crédito</div>
            <div className="w-sub" style={{ marginBottom: 6 }}>
              {trendLoading && "Consultando a API mês a mês..."}
              {!trendLoading && peak && <>Pico: <strong style={{ color: tokens.label(theme) }}>{peak.label}</strong> · {formatCurrency(peak.valor)}{periodFiltered ? " · no período filtrado" : ""}</>}
              {!trendLoading && !peak && "Sem valores por mês neste recorte"}
            </div>
            {trend.length > 0
              ? <TrendChart data={trend} dataKey="valor" name="Crédito" color={color} animate={!trendLoading} />
              : <Skeleton $h={220} $r={14} />}
          </Widget>

          <Widget $delay={200}>
            <div className="w-title">Concentração</div>
            <div className="w-sub">Quanto depende de poucos fornecedores</div>
            <div style={{ margin: "18px 0 16px" }}>
              <span style={{ fontSize: "2.2em", fontWeight: 600, letterSpacing: "-0.03em", color }}>{formatCount(stats.suppliersFor80)}</span>
              <div style={{ fontSize: "0.8em", color: tokens.label2(theme), marginTop: 2 }}>
                {stats.suppliersFor80 === 1 ? "fornecedor concentra" : "fornecedores concentram"} 80% do crédito
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
              {stats.concentration.map(({ n, share }) => (
                <div key={n}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.82em" }}>
                    <span style={{ fontWeight: 500 }}>{n === 1 ? "Maior fornecedor" : `Top ${n}`}</span>
                    <span style={{ color: tokens.label2(theme) }}>{formatPercent(share)}</span>
                  </div>
                  <Meter $color={color}><div style={{ width: `${share}%` }} /></Meter>
                </div>
              ))}
            </div>
          </Widget>
        </WidgetGrid>
      </Section>

      <Section $delay={140}>
        <WidgetGrid>
          <SpanCell $span={2}>
            <SectionHeader>
              <h2>Quem mais gera crédito</h2>
              <button className="link" onClick={onGoSuppliers}>Ver todos <FiChevronRight /></button>
            </SectionHeader>
            <Group>
              {top.map((f, i) => (
                <Row key={f.codigo || f.cnpj || f.nome} $clickable $inset="64px" onClick={() => onOpenSupplier(f)}>
                  <SupplierAvatar name={f.nome || f.codigo} size={36} />
                  <div className="main">
                    <div className="title"><TruncatedText title={f.nome}>{i + 1}. {displayName(f.nome) || f.codigo}</TruncatedText></div>
                    <Meter $color={color} style={{ marginTop: 7, maxWidth: 260 }}><div style={{ width: `${((f.totalCredito || 0) / topMax) * 100}%` }} /></Meter>
                  </div>
                  <div className="trailing">
                    <div style={{ textAlign: "right" }}>
                      <div className="value">{formatCurrency(f.totalCredito)}</div>
                      <div style={{ fontSize: "0.88em" }}>{formatCount(f.quantidade)} {f.quantidade === 1 ? "item" : "itens"}</div>
                    </div>
                    <FiChevronRight className="chevron" />
                  </div>
                </Row>
              ))}
              {top.length === 0 && <Row><div className="main"><div className="subtitle">Nenhum fornecedor identificado</div></div></Row>}
            </Group>
          </SpanCell>

          <Widget $delay={250}>
            <div className="w-title">Por alíquota</div>
            <div className="w-sub" style={{ marginBottom: 14 }}>Crédito em cada faixa aplicada</div>
            {stats.byRate.length > 0 ? <RankRows rows={stats.byRate} color={color} /> : <div className="w-sub">Sem alíquota informada</div>}
          </Widget>

          <Widget $delay={300}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 14 }}>
              <div className="w-title">Origem</div>
              {hasUf && (
                <SegmentedControl small value={cut} onChange={setCut} options={[{ key: "cfop", label: "CFOP" }, { key: "uf", label: "UF" }]} />
              )}
            </div>
            {cutRows.length > 0 ? <RankRows rows={cutRows} color={color} /> : <div className="w-sub">Sem CFOP informado</div>}
          </Widget>
        </WidgetGrid>
      </Section>

      {metodologia.nome && (
        <Section $delay={200}>
          <Widget $clickable onClick={onGoMethod}>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <FiBookOpen style={{ color, flexShrink: 0, fontSize: "1.2em" }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="w-title">Como este crédito é calculado</div>
                <div className="w-sub" style={{ overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{metodologia.descricao}</div>
              </div>
              <FiChevronRight style={{ opacity: 0.4, flexShrink: 0 }} />
            </div>
          </Widget>
        </Section>
      )}
    </>
  );
}

function SupplierGrid({ fornecedores, onOpen }) {
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("value");
  const [visible, setVisible] = useState(PAGE_SIZE);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    const list = fornecedores.filter((f) => !term
      || [f.nome, f.cnpj, f.codigo, f.uf].some((v) => String(v || "").toLowerCase().includes(term)));
    if (sort === "items") return [...list].sort((a, b) => (b.quantidade || 0) - (a.quantidade || 0));
    if (sort === "az") return [...list].sort((a, b) => String(a.nome).localeCompare(String(b.nome), "pt-BR"));
    return list;
  }, [fornecedores, q, sort]);

  return (
    <Section $first>
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <SearchField $max="420px">
          <FiSearch />
          <input
            placeholder="Buscar por nome, CNPJ ou UF"
            aria-label="Buscar fornecedor"
            value={q}
            onChange={(e) => { setQ(e.target.value); setVisible(PAGE_SIZE); }}
          />
          {q && <button className="clear" aria-label="Limpar busca" onClick={() => setQ("")}><FiX /></button>}
        </SearchField>
        <div style={{ flex: 1 }} />
        <SegmentedControl
          small
          value={sort}
          onChange={setSort}
          options={[{ key: "value", label: "Maior valor" }, { key: "items", label: "Mais itens" }, { key: "az", label: "A–Z" }]}
        />
      </div>

      {filtered.length === 0 ? (
        <div style={{ marginTop: 16 }}>
          <Group>
            <Empty>
              <div className="e-icon"><GoOrganization /></div>
              <div className="e-title">Nenhum fornecedor encontrado</div>
              {q && <div className="e-sub">Nada encontrado para &quot;{q}&quot;.</div>}
            </Empty>
          </Group>
        </div>
      ) : (
        <CardGrid style={{ marginTop: 16 }}>
          {filtered.slice(0, visible).map((f, idx) => (
            <EntityCard key={f.codigo || f.cnpj || f.nome} $color="transparent" $delay={Math.min(idx, 12) * 30} onClick={() => onOpen(f)}>
              <div className="head">
                <SupplierAvatar name={f.nome || f.codigo} size={44} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="name" title={f.nome}>{displayName(f.nome) || f.codigo}</div>
                  <div className="sub">{[f.cnpj ? formatCNPJ(f.cnpj) : f.codigo, f.uf].filter(Boolean).join(" · ")}</div>
                </div>
              </div>
              <div className="stats" style={{ gridTemplateColumns: "1.5fr 1fr 1fr" }}>
                <div><div className="s-value" title={formatCurrency(f.totalCredito)}>{formatCompact(f.totalCredito)}</div><div className="s-label">crédito</div></div>
                <div><div className="s-value">{formatCount(f.quantidade)}</div><div className="s-label">{f.quantidade === 1 ? "item" : "itens"}</div></div>
                <div><div className="s-value">{formatRate(f.mediaAliquota)}</div><div className="s-label">alíquota</div></div>
              </div>
              <div className="foot">
                <span>{f.cfops?.length ? `CFOP ${f.cfops.slice(0, 3).join(", ")}${f.cfops.length > 3 ? ` +${f.cfops.length - 3}` : ""}` : "—"}</span>
                <span>{f.periodoFim ? formatDate(f.periodoFim) : ""}</span>
              </div>
            </EntityCard>
          ))}
        </CardGrid>
      )}

      {visible < filtered.length && (
        <LoadMore type="button" onClick={() => setVisible((v) => v + PAGE_SIZE)}>
          Mostrar mais ({formatCount(filtered.length - visible)} restantes)
        </LoadMore>
      )}
    </Section>
  );
}

function Methodology({ metodologia, color }) {
  const theme = useTheme();
  return (
    <Section $first>
      <WidgetGrid>
        <SpanCell $span={3}>
          <Widget style={{ marginBottom: 14 }}>
            <div className="w-title" style={{ marginBottom: 6 }}>{metodologia.nome}</div>
            <div style={{ fontSize: "0.9em", lineHeight: 1.55, color: tokens.label2(theme) }}>{metodologia.descricao}</div>
          </Widget>
          {metodologia.etapas?.length > 0 && (
            <>
              <GroupLabel $first><span>Etapas do cálculo</span><span>{metodologia.etapas.length}</span></GroupLabel>
              <Group>
                {metodologia.etapas.map((etapa, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <Row key={i} $inset="56px">
                    <StepNumber $color={color}>{i + 1}</StepNumber>
                    <div className="main" style={{ fontSize: "0.88em", lineHeight: 1.45 }}>{etapa}</div>
                  </Row>
                ))}
              </Group>
            </>
          )}
        </SpanCell>

        <div style={{ display: "flex", flexDirection: "column", gap: 14, minWidth: 0 }}>
          {metodologia.baseLegal && (
            <Widget>
              <div className="w-head">Base legal</div>
              <div style={{ fontSize: "0.88em", lineHeight: 1.5 }}>{metodologia.baseLegal}</div>
            </Widget>
          )}
          {metodologia.registroOrigem && (
            <Widget>
              <div className="w-head">Registro de origem</div>
              <div style={{ fontSize: "0.88em", lineHeight: 1.5 }}>{metodologia.registroOrigem}</div>
            </Widget>
          )}
          {metodologia.cfopsRelacionados?.length > 0 && (
            <Widget>
              <div className="w-head">CFOPs relacionados</div>
              <div style={{ fontSize: "0.88em", lineHeight: 1.6 }}>{metodologia.cfopsRelacionados.join(" · ")}</div>
            </Widget>
          )}
        </div>
      </WidgetGrid>
    </Section>
  );
}

export default function CreditoDetailScreen({ credito, series, periodFiltered, onBack }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("overview");
  const [selectedFornecedor, setSelectedFornecedor] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const projeto = localStorage.getItem("vidal_selected_project");
      const response = await api.get(`/creditos/agrupado/${credito.tipoCredito}`, { params: { projeto } });
      if (!response.data.success) throw new Error("Resposta sem sucesso");
      setData(response.data.data);
    } catch (err) {
      console.error("Erro ao carregar o tipo de crédito:", err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [credito.tipoCredito]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const metodologia = data?.metodologia || {};
  const stats = useMemo(() => buildCreditoStats(data?.fornecedores || [], data?.resumo), [data]);
  const trend = useMemo(() => {
    const rows = (series?.months || []).map((m) => ({ key: m.key, label: m.label, valor: m.tipos?.[credito.tipoCredito] || 0 }));
    const first = rows.findIndex((m) => m.valor > 0);
    return first < 0 ? [] : rows.slice(first);
  }, [series, credito.tipoCredito]);

  if (selectedFornecedor) {
    return (
      <FornecedorDetailScreen
        credito={credito}
        fornecedor={selectedFornecedor}
        backLabel={metodologia.nome || credito.titulo}
        periodFiltered={periodFiltered}
        onBack={() => setSelectedFornecedor(null)}
      />
    );
  }

  const tax = taxMeta(credito.tipo);

  let content;
  if (loading) {
    content = (
      <Section $first>
        <WidgetGrid>{[0, 1, 2, 3].map((i) => <Widget key={i}><Skeleton $h={70} $r={10} /></Widget>)}</WidgetGrid>
        <div style={{ height: 14 }} />
        <Skeleton $h={280} $r={16} />
      </Section>
    );
  } else if (error) {
    content = (
      <Section $first>
        <Group>
          <Empty>
            <div className="e-icon"><FiAlertTriangle /></div>
            <div className="e-title">Não foi possível carregar este crédito</div>
            <div className="e-sub" style={{ marginBottom: 14 }}>Verifique a conexão e tente de novo.</div>
            <ActionButton onClick={fetchData} style={{ margin: "0 auto" }}><FiRefreshCw /> Tentar novamente</ActionButton>
          </Empty>
        </Group>
      </Section>
    );
  } else if (tab === "suppliers") {
    content = <SupplierGrid fornecedores={stats.sorted} onOpen={setSelectedFornecedor} />;
  } else if (tab === "method") {
    content = <Methodology metodologia={metodologia} color={tax.color} />;
  } else {
    content = (
      <Overview
        stats={stats}
        credito={credito}
        metodologia={metodologia}
        trend={trend}
        trendLoading={!!series?.loading}
        periodFiltered={periodFiltered}
        onOpenSupplier={setSelectedFornecedor}
        onGoSuppliers={() => setTab("suppliers")}
        onGoMethod={() => setTab("method")}
      />
    );
  }

  return (
    <CreditsPage>
      <ScrolledTopBar scrollToTop={true} deps={[!!selectedFornecedor]}>
        <div className="title-row">
          <div>
            <BackButton onClick={onBack}>Créditos</BackButton>
            <h1>{metodologia.nome || credito.titulo}</h1>
            <div className="meta">
              <Tag $color={tax.color} style={{ fontSize: "1em" }}>{tax.label}</Tag>
              <span className="update-tag">{categoryMeta(credito.categoria).label}</span>
              {!loading && !error && <span className="update-tag">{formatCount(stats.items)} itens</span>}
            </div>
          </div>
          <div className="actions">
            <IconButton onClick={fetchData} title="Atualizar" aria-label="Atualizar">
              <FiRefreshCw className={loading ? "spin" : ""} />
            </IconButton>
          </div>
        </div>
        <div className="nav-row">
          <SegmentedControl
            value={tab}
            onChange={setTab}
            options={[
              { key: "overview", icon: <FiGrid />, label: "Visão geral" },
              { key: "suppliers", icon: <FiList />, label: "Fornecedores", count: loading ? undefined : stats.sorted.length },
              ...(metodologia.nome ? [{ key: "method", icon: <FiBookOpen />, label: "Metodologia" }] : []),
            ]}
          />
        </div>
      </ScrolledTopBar>

      {periodFiltered && !error && (
        <Notice style={{ marginTop: 0, marginBottom: 12 }}>
          <FiInfo />
          <span>Os totais e fornecedores desta tela são de todo o projeto: a API não aplica o filtro de período aqui.</span>
        </Notice>
      )}

      {content}
    </CreditsPage>
  );
}
